/**
 * importStore — AK-368c «Импорт полей из файла» (UX Вар-2).
 *
 * Отдельный лёгкий zustand-стор (как addFieldStore/measureStore/editorStore,
 * ADR-038), НЕ часть mapReducer. Держит UI-машину импорта + пачку контуров из
 * превью + выбор/переименование/удаление/правку + назначение хозяйства.
 * Геометрию на карте рисует императивный ImportController по подписке (v2.tsx);
 * стор знает только про данные и фазы.
 *
 * Живёт ВНУТРИ режима «+ Поле» (addFieldStore.active): импорт — третий способ
 * рядом с «Тапнуть» и «Нарисовать». Поэтому у стора нет своего on/off тула в
 * RightRail — его активирует ImportFilePanel из панели «+ Поле».
 *
 * Машина фаз (`phase`):
 *   idle     — импорт не начат (панель показывает 3 способа + дропзону)
 *   parsing  — файл ушёл на POST preview, крутится лоадер «Читаем файл…»
 *   preview  — контуры пришли, лежат на карте, список справа (выбор/правка)
 *   saving   — идёт батч-сохранение (прогресс)
 *   error    — ошибка чтения файла целиком (фрейм 6): unreadable/too-large/…
 *
 * «Уточнить контур» одной строки: editingIndex != null → строка ушла в редактор
 * (terra-draw AK-322), остальная пачка приглушена. Возврат из редактора кладёт
 * уточнённую геометрию обратно в фичу (v2.tsx через applyEditedContour).
 */
import { create } from 'zustand';
import type {
  DuplicateMatch,
  ImportFileErrorKind,
  ImportGeometry,
  PreviewFeature,
} from './importApi';

export type ImportPhase = 'idle' | 'parsing' | 'preview' | 'saving' | 'error';

/**
 * AK-369 три режима имён (wireframe фрейм 4/4б, дельта к бинарному AK-368):
 *   auto  — «Поле N» автоген, имена из файла игнорируются;
 *   map   — имена из файла, правка тапом по контуру → инлайн-инпут у контура;
 *   table — имена из файла, правка в компактной таблице (Tab ↓ по колонке имён).
 * map/table отличаются ТОЛЬКО UI-способом ввода; для save оба = «имя строки»
 * (row.name, пустое → автоген). auto = всегда автоген. AK-368 `'file'` теперь
 * эквивалент `'map'` (имена из файла), поэтому в стор кладём одну из трёх.
 */
export type NameMode = 'auto' | 'map' | 'table';

/** Одна строка пачки — превью-фича + UI-состояние (выбор/имя/пересечение). */
export interface ImportRow {
  /** Стабильный ключ (из preview.index). */
  index: number;
  geometry: ImportGeometry | null;
  /** Текущее имя (инлайн-редактируемое). Из файла или пусто (→ автоген). */
  name: string;
  number: string | null;
  /** Площадь га; пересчитывается после «Уточнить контур». */
  areaHa: number | null;
  valid: boolean;
  /** Причина невалидности / warning авто-починки (из бэка). */
  error: string | null;
  /** Выбрана к сохранению (невалидная всегда false, чекбокс задизейблен). */
  selected: boolean;
  /** Имя существующего поля, с которым пересекается (best-effort), либо null. */
  intersectsWith: string | null;
  /**
   * AK-369 дубликаты из превью-бэка (пересечение ≥ порога с существующими
   * полями). Пусто → не дубликат. Непустой → строка попадает в сворачиваемую
   * группу «Дубликаты», по умолчанию СНЯТА (не плодим копии).
   */
  duplicates: DuplicateMatch[];
}

/** Строка — дубликат (есть совпадения из бэка). */
export function isDuplicate(row: ImportRow): boolean {
  return row.duplicates.length > 0;
}

/** Лучший (максимальный по перекрытию) дубль строки, либо null. */
export function topDuplicate(row: ImportRow): DuplicateMatch | null {
  return row.duplicates[0] ?? null;
}

interface ImportStore {
  phase: ImportPhase;
  /** Имя загруженного файла (для шапки списка/лоадера). */
  fileName: string | null;
  /** Пачка контуров. */
  rows: ImportRow[];
  /** Предупреждения от бэка (усечение >500 и т.п.). */
  warnings: string[];
  /** Ошибка файла целиком (фаза error). */
  fileError: { kind: ImportFileErrorKind; message: string } | null;
  /** Назначенное на пачку хозяйство. */
  farmId: string | null;
  /** AK-369: назначенное на пачку отделение/бригада (опционально), либо null. */
  brigadeId: string | null;
  /** AK-369 режим имён: auto / map / table (см. NameMode). */
  nameMode: NameMode;
  /** AK-369: свёрнута ли группа «Дубликаты (K)» в списке. */
  dupGroupCollapsed: boolean;
  /** Строка на «Уточнить контур» (одна за раз), либо null. */
  editingIndex: number | null;
  /**
   * AK-369 режим «имена на карте»: индекс строки с открытым инлайн-инпутом у
   * контура (тап по контуру), либо null. Отдельно от editingIndex (правка
   * геометрии) — здесь правится только имя.
   */
  nameEditIndex: number | null;
  /** Прогресс сохранения 0..1 (для прогресс-бара). */
  saveProgress: number;
  /** Тост успеха («✓ N полей добавлено в …»), либо null. */
  successToast: string | null;
  /**
   * AK-369: batch_id последнего успешного сохранения — для кнопки «Отменить
   * импорт» в тосте (POST /import/{batch_id}/rollback). null если бэк не отдал
   * batch_id (старый AK-368) — тогда кнопка отката в тосте не появляется.
   */
  lastBatchId: string | null;
  /** Тост ошибки (сетевые при save и т.п.), либо null. */
  errorToast: string | null;

  // ── жизненный цикл ──────────────────────────────────────────────────────
  /** Файл ушёл на превью — фаза parsing. */
  beginParse: (fileName: string) => void;
  /** Превью пришло — построить пачку (фаза preview). */
  setPreview: (features: PreviewFeature[], warnings: string[]) => void;
  /** Ошибка файла целиком — фаза error. */
  failFile: (kind: ImportFileErrorKind, message: string) => void;
  /** Полный сброс импорта (в idle) — «Выбрать другой файл» / выход. */
  reset: () => void;

  // ── редактирование пачки ────────────────────────────────────────────────
  /** Тоггл выбора строки (невалидную не трогаем). */
  toggleSelected: (index: number) => void;
  /** Выбрать/снять все valid-строки. */
  setAllSelected: (selected: boolean) => void;
  /** Инлайн-переименование строки. */
  renameRow: (index: number, name: string) => void;
  /** Удалить строку из пачки (и с карты). */
  removeRow: (index: number) => void;
  /** Пометить пересечения (после turf-теста в v2.tsx). Map index→имя поля. */
  setIntersections: (byIndex: Record<number, string>) => void;
  /** AK-369: снять выбор со всех строк-дубликатов («Исключить все»). */
  excludeAllDuplicates: () => void;
  /** AK-369: свернуть/развернуть группу «Дубликаты». */
  toggleDupGroup: () => void;

  // ── назначение ──────────────────────────────────────────────────────────
  setFarmId: (farmId: string | null) => void;
  setBrigadeId: (brigadeId: string | null) => void;
  setNameMode: (mode: NameMode) => void;
  /** AK-369: открыть/закрыть инлайн-правку имени у контура (режим «На карте»). */
  setNameEditIndex: (index: number | null) => void;

  // ── «Уточнить контур» ─────────────────────────────────────────────────────
  /** Начать правку строки в редакторе (одна за раз). */
  beginEdit: (index: number) => void;
  /** Редактор вернул уточнённый контур → записать в фичу, выйти из правки. */
  applyEdit: (index: number, geometry: ImportGeometry, areaHa: number) => void;
  /** Отмена правки без изменений. */
  cancelEdit: () => void;

  // ── сохранение ────────────────────────────────────────────────────────────
  beginSave: () => void;
  setSaveProgress: (p: number) => void;
  /** Успех: тост + batch_id для отката (null если бэк не отдал). */
  completeSave: (message: string, batchId: string | null) => void;
  failSave: (message: string) => void;

  clearSuccessToast: () => void;
  clearErrorToast: () => void;
}

/**
 * Превью-фича → строка пачки.
 *   valid && НЕ дубликат  → выбрана по умолчанию;
 *   дубликат (duplicates непусто) → СНЯТА по умолчанию (не плодим копии, AK-369);
 *   невалидная            → снята, чекбокс задизейблен.
 * Дубликат остаётся включаемым вручную (админ решает: переобмер / соседний).
 */
function rowFromFeature(f: PreviewFeature): ImportRow {
  const dup = f.duplicates.length > 0;
  return {
    index: f.index,
    geometry: f.geometry,
    name: f.suggested_name ?? '',
    number: f.number,
    areaHa: f.area_ha,
    valid: f.valid,
    error: f.error,
    // valid И не дубликат → выбрана. Дубликат по умолчанию снят (AK-369).
    selected: f.valid && !dup,
    intersectsWith: null,
    duplicates: f.duplicates,
  };
}

/** Число valid+выбранных строк — для счётчика «выбрано N из M» и кнопки save. */
export function selectedCount(rows: readonly ImportRow[]): number {
  return rows.filter((r) => r.valid && r.selected).length;
}

/** Число valid-строк (потолок счётчика). */
export function validCount(rows: readonly ImportRow[]): number {
  return rows.filter((r) => r.valid).length;
}

export const useImportStore = create<ImportStore>((set) => ({
  phase: 'idle',
  fileName: null,
  rows: [],
  warnings: [],
  fileError: null,
  farmId: null,
  brigadeId: null,
  nameMode: 'map', // AK-369: дефолт «На карте» — имена из файла + правка тапом.
  dupGroupCollapsed: false,
  editingIndex: null,
  nameEditIndex: null,
  saveProgress: 0,
  successToast: null,
  lastBatchId: null,
  errorToast: null,

  beginParse: (fileName) =>
    set({
      phase: 'parsing',
      fileName,
      rows: [],
      warnings: [],
      fileError: null,
      editingIndex: null,
      nameEditIndex: null,
    }),

  setPreview: (features, warnings) => {
    const rows = features.map(rowFromFeature);
    // 0 валидных при непустом файле = отдельное состояние ошибки (фрейм 6).
    if (rows.length > 0 && validCount(rows) === 0) {
      set({
        phase: 'error',
        rows,
        warnings,
        fileError: {
          kind: 'empty',
          message: 'В файле нет пригодных контуров.',
        },
      });
      return;
    }
    if (rows.length === 0) {
      set({
        phase: 'error',
        rows: [],
        warnings,
        fileError: {
          kind: 'empty',
          message: 'В файле не найдено ни одного контура.',
        },
      });
      return;
    }
    set({ phase: 'preview', rows, warnings });
  },

  failFile: (kind, message) =>
    set({ phase: 'error', fileError: { kind, message }, rows: [] }),

  reset: () =>
    set({
      phase: 'idle',
      fileName: null,
      rows: [],
      warnings: [],
      fileError: null,
      farmId: null,
      brigadeId: null,
      dupGroupCollapsed: false,
      editingIndex: null,
      nameEditIndex: null,
      saveProgress: 0,
    }),

  toggleSelected: (index) =>
    set((s) => ({
      rows: s.rows.map((r) =>
        r.index === index && r.valid ? { ...r, selected: !r.selected } : r,
      ),
    })),

  setAllSelected: (selected) =>
    set((s) => ({
      rows: s.rows.map((r) => (r.valid ? { ...r, selected } : r)),
    })),

  renameRow: (index, name) =>
    set((s) => ({
      rows: s.rows.map((r) => (r.index === index ? { ...r, name } : r)),
    })),

  removeRow: (index) =>
    set((s) => ({
      rows: s.rows.filter((r) => r.index !== index),
      editingIndex: s.editingIndex === index ? null : s.editingIndex,
    })),

  setIntersections: (byIndex) =>
    set((s) => ({
      rows: s.rows.map((r) => ({
        ...r,
        intersectsWith: byIndex[r.index] ?? null,
      })),
    })),

  // AK-369: «Исключить все» — снять выбор со всех строк-дубликатов одним кликом.
  excludeAllDuplicates: () =>
    set((s) => ({
      rows: s.rows.map((r) => (isDuplicate(r) ? { ...r, selected: false } : r)),
    })),

  toggleDupGroup: () => set((s) => ({ dupGroupCollapsed: !s.dupGroupCollapsed })),

  setFarmId: (farmId) => set({ farmId }),
  setBrigadeId: (brigadeId) => set({ brigadeId }),
  setNameMode: (nameMode) =>
    // Смена режима закрывает открытый инлайн-инпут имени у контура.
    set({ nameMode, nameEditIndex: null }),
  setNameEditIndex: (nameEditIndex) => set({ nameEditIndex }),

  beginEdit: (index) => set({ editingIndex: index, nameEditIndex: null }),
  applyEdit: (index, geometry, areaHa) =>
    set((s) => ({
      editingIndex: null,
      rows: s.rows.map((r) =>
        r.index === index ? { ...r, geometry, areaHa } : r,
      ),
    })),
  cancelEdit: () => set({ editingIndex: null }),

  beginSave: () => set({ phase: 'saving', saveProgress: 0 }),
  setSaveProgress: (saveProgress) => set({ saveProgress }),
  completeSave: (message, batchId) =>
    set({
      phase: 'idle',
      fileName: null,
      rows: [],
      warnings: [],
      farmId: null,
      brigadeId: null,
      editingIndex: null,
      nameEditIndex: null,
      dupGroupCollapsed: false,
      saveProgress: 0,
      successToast: message,
      lastBatchId: batchId,
    }),
  failSave: (message) => set({ phase: 'preview', errorToast: message }),

  clearSuccessToast: () => set({ successToast: null, lastBatchId: null }),
  clearErrorToast: () => set({ errorToast: null }),
}));
